/**
 * PDP Content Tabs
 *
 * Handles tab switching on desktop and accordion behavior on mobile.
 * Supports deep-linking via URL hash and anchor scrolling from the
 * "View All Models" CTA in the variant configurator.
 */

class PdpContentTabs extends HTMLElement {
  constructor() {
    super();
    this.mobileBreakpoint = 750;
    this.abortController = null;
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    this.accordionHeaders = this.querySelectorAll('[data-accordion]');

    // Track the active panel across viewports
    const initialTab = this.querySelector('[aria-selected="true"]');
    this.activeTabId = initialTab ? initialTab.dataset.tab : (this.tabs[0] ? this.tabs[0].dataset.tab : null);

    this.bindEvents();
    this.initTechSlider();
    this.handleHash();
    this.handleResize();
  }

  bindEvents() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Tab clicks (desktop)
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab.dataset.tab, true), { signal });
    });

    // Tab keyboard navigation (WAI-ARIA Tabs pattern)
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      const tabsArray = [...this.tabs];
      const currentIndex = tabsArray.indexOf(e.target);
      if (currentIndex === -1) return;

      let nextIndex;
      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabsArray.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabsArray.length) % tabsArray.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabsArray.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      this.activateTab(tabsArray[nextIndex].dataset.tab, true);
      tabsArray[nextIndex].focus();
    }, { signal });

    // Accordion clicks (mobile)
    this.accordionHeaders.forEach((header) => {
      header.addEventListener('click', () => this.toggleAccordion(header.dataset.accordion), { signal });
    });

    // Hash changes
    window.addEventListener('hashchange', () => this.handleHash(), { signal });

    // Resize for mobile/desktop switching
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(document.documentElement);
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Initialize the technology slider if present
   */
  initTechSlider() {
    this.techSlider = this.querySelector('[data-tech-slider]');
    if (!this.techSlider) return;

    this.techGroups = this.techSlider.querySelectorAll('[data-tech-group]');
    this.techBgs = this.techSlider.querySelectorAll('[data-tech-bg]');
    this.techDots = this.techSlider.querySelectorAll('[data-tech-dot]');

    const { signal } = this.abortController;

    // Pill clicks
    this.techSlider.querySelectorAll('[data-tech-index]').forEach((pill) => {
      pill.addEventListener('click', () => this.activateTech(parseInt(pill.dataset.techIndex, 10)), { signal });
    });

    // Dot clicks
    this.techDots.forEach((dot) => {
      dot.addEventListener('click', () => this.activateTech(parseInt(dot.dataset.techDot, 10)), { signal });
    });
  }

  /**
   * Activate a technology slide by index
   */
  activateTech(index) {
    if (!this.techSlider) return;

    // Update item groups
    this.techGroups.forEach((group) => {
      group.classList.toggle(
        'pdp-tech-slider__item-group--active',
        parseInt(group.dataset.techGroup, 10) === index
      );
    });

    // Crossfade background images
    this.techBgs.forEach((bg) => {
      bg.classList.toggle(
        'pdp-tech-slider__bg--active',
        parseInt(bg.dataset.techBg, 10) === index
      );
    });

    // Update dots
    this.techDots.forEach((dot) => {
      dot.classList.toggle(
        'pdp-tech-slider__dot--active',
        parseInt(dot.dataset.techDot, 10) === index
      );
    });

    // Recalculate accordion max-height if in mobile accordion mode
    const techPanel = this.querySelector('[data-panel="technology"]');
    if (techPanel && techPanel.classList.contains('pdp-content-tabs__panel--accordion-open')) {
      const content = techPanel.querySelector('.pdp-content-tabs__panel-content');
      if (content) {
        content.style.maxHeight = 'none';
        requestAnimationFrame(() => {
          content.style.maxHeight = content.scrollHeight + 'px';
        });
      }
    }
  }

  /**
   * Activate a tab by its data-tab value
   * @param {string} tabId
   * @param {boolean} [updateTabindex=false] - Whether to update tabindex (for user-initiated actions)
   */
  activateTab(tabId, updateTabindex = false) {
    this.activeTabId = tabId;

    // Update tab buttons
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabId;
      tab.setAttribute('aria-selected', isActive);
      tab.classList.toggle('pdp-content-tabs__tab--active', isActive);
      if (updateTabindex) {
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      }
    });

    // Update panels
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.panel === tabId;
      panel.classList.toggle('pdp-content-tabs__panel--hidden', !isActive);
      panel.classList.toggle('pdp-content-tabs__panel--active', isActive);
      panel.hidden = !isActive;
    });
  }

  /**
   * Update aria-expanded on all accordion headers to reflect current state
   */
  syncAccordionAria() {
    this.accordionHeaders.forEach((header) => {
      const panelId = header.dataset.accordion;
      const panel = this.querySelector(`[data-panel="${panelId}"]`);
      const isOpen = panel?.classList.contains('pdp-content-tabs__panel--accordion-open') ?? false;
      header.setAttribute('aria-expanded', String(isOpen));
    });
  }

  /**
   * Toggle an accordion panel on mobile
   */
  toggleAccordion(panelId) {
    const panel = this.querySelector(`[data-panel="${panelId}"]`);
    if (!panel) return;

    const isOpen = panel.classList.contains('pdp-content-tabs__panel--accordion-open');

    if (isOpen) {
      panel.classList.remove('pdp-content-tabs__panel--accordion-open');
      const openContent = panel.querySelector('.pdp-content-tabs__panel-content');
      if (openContent) openContent.style.maxHeight = null;
    } else {
      // Close other accordion panels
      this.panels.forEach((p) => {
        p.classList.remove('pdp-content-tabs__panel--accordion-open');
        const content = p.querySelector('.pdp-content-tabs__panel-content');
        if (content) content.style.maxHeight = null;
      });

      panel.classList.add('pdp-content-tabs__panel--accordion-open');

      // Scroll the opened header into view, accounting for sticky header
      const header = panel.querySelector('[data-accordion]');
      if (header) {
        requestAnimationFrame(() => {
          const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
          const top = header.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      }
      this.activeTabId = panelId;

      // Keep desktop tab buttons in sync
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tab === panelId;
        tab.setAttribute('aria-selected', isActive);
        tab.classList.toggle('pdp-content-tabs__tab--active', isActive);
      });
      const content = panel.querySelector('.pdp-content-tabs__panel-content');
      if (content) {
        content.style.maxHeight = content.scrollHeight + 'px';
        // After the transition, switch to none so inner content (e.g. expanding table rows) isn't clipped
        content.addEventListener('transitionend', function handler() {
          content.removeEventListener('transitionend', handler);
          if (panel.classList.contains('pdp-content-tabs__panel--accordion-open')) {
            content.style.maxHeight = 'none';
          }
        });
      }
    }

    this.syncAccordionAria();
  }

  /**
   * Scroll to and activate a tab (used by external "Read More" links)
   */
  scrollToTab(tabId) {
    const isMobile = window.innerWidth < this.mobileBreakpoint;

    if (isMobile) {
      const panel = this.querySelector(`[data-panel="${tabId}"]`);
      const isOpen = panel?.classList.contains('pdp-content-tabs__panel--accordion-open');
      if (!isOpen) {
        this.toggleAccordion(tabId);
        return; // toggleAccordion handles its own scrolling
      }
    } else {
      this.activateTab(tabId, true);
    }

    // Scroll to the component, accounting for sticky header
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    const top = this.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  /**
   * Handle URL hash to activate the correct tab
   */
  handleHash() {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const matchingTab = this.querySelector(`[data-tab="${hash}"]`);
      if (matchingTab) {
        if (window.innerWidth >= this.mobileBreakpoint) {
          this.activateTab(hash, true);
        } else {
          this.toggleAccordion(hash);
        }
      }
    }
  }

  /**
   * Handle responsive behavior
   */
  handleResize() {
    const isMobile = window.innerWidth < this.mobileBreakpoint;
    this.classList.toggle('pdp-content-tabs--mobile', isMobile);
    this.classList.toggle('pdp-content-tabs--desktop', !isMobile);

    if (isMobile) {
      // Show all panels as accordion items — preserve current open/closed
      // state so nothing auto-opens (all start collapsed on load).
      this.panels.forEach((panel) => {
        panel.classList.remove('pdp-content-tabs__panel--hidden');
        panel.hidden = false;
      });
      this.syncAccordionAria();
    } else {
      // Close all accordions and activate the synced tab
      this.panels.forEach((p) => {
        p.classList.remove('pdp-content-tabs__panel--accordion-open');
        const content = p.querySelector('.pdp-content-tabs__panel-content');
        if (content) content.style.maxHeight = null;
      });
      this.activateTab(this.activeTabId || (this.tabs[0] ? this.tabs[0].dataset.tab : ''), true);
    }
  }
}

if (!customElements.get('pdp-content-tabs')) {
  customElements.define('pdp-content-tabs', PdpContentTabs);
}
